import { useI18n } from "@/i18n/translations";
import { iniziative } from "@/data/events";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/Editorial";
import { EventCard } from "@/components/EventCard";
import { Reveal } from "@/components/Reveal";
import type { Iniziativa } from "@/data/events";

const DECADENZE: Record<number, string> = {
  1980: "Anni Ottanta",
  1990: "Anni Novanta",
  2000: "Anni Duemila",
  2010: "Anni Duemiladieci",
  2020: "Anni Duemilaventi",
};

const DECADENZE_EN: Record<number, string> = {
  1980: "The Eighties",
  1990: "The Nineties",
  2000: "The Two Thousands",
  2010: "The Twenty-Tens",
  2020: "The Twenty-Twenties",
};

export const Iniziative = () => {
  const { t, lang } = useI18n();
  // Elastico: la divisione correnti/archivio è calcolata dalla data (regola 2026).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const withDate = iniziative.map((e) => {
    if (!e.date) return { ...e, d: null as Date | null };
    const { y, m, d } = e.date;
    return { ...e, d: new Date(y, (m ?? 1) - 1, d ?? 15) };
  });
  const correnti = withDate.filter((e) => e.d && e.d >= today);
  const archivio = withDate.filter((e) => !e.d || e.d < today);
  // Ordine inverso: dall'iniziativa più recente alla meno recente (decennio per decennio, per data).
  const perData = (e: (typeof archivio)[number]) => (e.date ? e.date.y * 10000 + (e.date.m ?? 0) * 100 + (e.date.d ?? 0) : e.n ?? 0);
  const decadi = [...new Set(archivio.map((e) => (e.date ? Math.floor(e.date.y / 10) * 10 : null)))].sort((a, b) => (b ?? -1) - (a ?? -1));

  return (
    <>
      <Seo titleKey="seo.iniziative.title" descKey="seo.iniziative.desc" path="/attivita/iniziative" />
      <PageHeader kickerKey="att.kicker" titleKey="att.iniziative_title">
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-mutedtx">{t("att.intro_reale")}</p>
      </PageHeader>

      {/* INDICE PER DECENNI — ancore per orientarsi nell'archivio (dal più recente al meno recente) */}
      <nav className="sticky top-16 z-30 border-y border-borderc bg-paper/95 backdrop-blur-md" data-testid="iniziative-decadi-nav" aria-label="Indice per decenni">
        <div className="container-x flex flex-wrap items-center gap-x-6 gap-y-1 py-3 text-[12px] font-semibold uppercase tracking-[0.12em]">
          <span className="text-mutedtx/70">{t("att.indice_decadi")}</span>
          {decadi.map((dec) => (
            <a key={String(dec)} href={`#decennio-${dec ?? "sd"}`} className="link-underline text-mutedtx transition-colors hover:text-bordeaux" data-testid={`decennio-link-${dec ?? "sd"}`}>
              {dec ? (lang === "en" ? DECADENZE_EN[dec] ?? `${dec}s` : DECADENZE[dec] ?? `${dec}-`) : t("att.senza_data")}
            </a>
          ))}
        </div>
      </nav>

      <div className="container-x space-y-20 py-16 sm:py-20">
        {/* INIZIATIVE CORRENTI — stato vuoto autorizzato: nessun evento futuro ancora comunicato */}
        <section id="iniziative-in-corso" className="scroll-mt-44" data-testid="attivita-iniziative">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("att.correnti_title")}</h2>
          </Reveal>
          {correnti.length > 0 ? (
            <div className="mt-8 space-y-8" data-testid="attivita-iniziative-list">
              {correnti.map((ev, i) => (
                <EventCard key={`${ev.id}-${i}`} ev={ev as Iniziativa} />
              ))}
            </div>
          ) : (
            <Reveal delay={0.08} className="mt-8">
              <EmptyStateLight testid="attivita-iniziative-empty-state" kicker={t("common.aggiornamento")} text={t("att.iniziative_empty")} />
            </Reveal>
          )}
        </section>

        {/* ARCHIVIO INIZIATIVE 1986-2026 — elenco ufficiale del Centro, raggruppato per decennio */}
        <section data-testid="attivita-archivio">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">{t("att.archivio_title")}</h2>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-mutedtx">{t("att.archivio_intro")}</p>
            <p className="mt-2 max-w-3xl text-xs italic leading-relaxed text-mutedtx">{t("att.archivio_lang_note")}</p>
          </Reveal>
          <div className="mt-10 space-y-14" data-testid="attivita-archivio-list">
            {decadi.map((dec) => (
              <div key={String(dec)} id={`decennio-${dec ?? "sd"}`} className="scroll-mt-44">
                <Reveal>
                  <h3 className="font-serif text-2xl italic text-bordeaux" data-testid={`archivio-decennio-${dec ?? "sd"}`}>
                    {dec ? (lang === "en" ? DECADENZE_EN[dec] ?? `${dec}s` : DECADENZE[dec] ?? `${dec}-`) : t("att.senza_data")}
                  </h3>
                </Reveal>
                <div className="mt-6 space-y-6">
                  {archivio
                    .filter((e) => (e.date ? Math.floor(e.date.y / 10) * 10 : null) === dec)
                    .sort((a, b) => perData(b) - perData(a))
                    .map((ev, i) => (
                      <EventCard key={`${ev.id}-${i}`} ev={ev as Iniziativa} />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

const EmptyStateLight = ({ kicker, text, testid }: { kicker: string; text: string; testid: string }) => (
  <div data-testid={testid} className="flex flex-col items-center gap-3 border border-dashed border-borderc bg-cardwhite px-6 py-12 text-center">
    <span className="eyebrow text-bordeaux">{kicker}</span>
    <p className="max-w-xl text-sm leading-relaxed text-mutedtx">{text}</p>
  </div>
);

export default Iniziative;
